import * as React from "react";
import { MapPin, Check, ChevronsUpDown, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "@shared/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@shared/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@shared/components/ui/popover";
import { useCityByNameQuery } from "./useCities";

/**
 * SelectQuartier component for selecting or creating quartiers
 * Suggests quartiers from the selected city or allows custom input
 */
export function SelectQuartier({
  quartier,
  onSelectQuartier,
  cityName,
  className,
  disabled = false,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [value, setValue] = React.useState(quartier || "");

  // Fetch city data to get quartiers when city is selected
  const { data: cityData, isLoading } = useCityByNameQuery(cityName, !!cityName);

  // Extract quartiers from city data
  const suggestedQuartiers = React.useMemo(() => {
    return cityData?.quartiers || [];
  }, [cityData]);

  // Filter quartiers based on search term
  const filteredQuartiers = React.useMemo(() => {
    if (!searchTerm) return suggestedQuartiers;
    
    const lowerSearch = searchTerm.toLowerCase();
    return suggestedQuartiers.filter((q) =>
      q.toLowerCase().includes(lowerSearch)
    );
  }, [suggestedQuartiers, searchTerm]);

  // Check if search term is a new custom quartier
  const isCustomQuartier = React.useMemo(() => {
    if (!searchTerm) return false;
    
    return (
      searchTerm.trim() !== "" &&
      !filteredQuartiers.some(
        (q) => q.toLowerCase() === searchTerm.toLowerCase()
      )
    );
  }, [searchTerm, filteredQuartiers]);

  // Handle quartier selection
  const handleSelectQuartier = (selectedValue) => {
    setValue(selectedValue);
    onSelectQuartier(selectedValue);
    setOpen(false);
    setSearchTerm("");
  };

  // Handle custom quartier creation
  const handleCreateCustom = () => {
    const customValue = searchTerm.trim();
    if (customValue) {
      handleSelectQuartier(customValue);
    }
  };

  // Update value when quartier prop changes
  React.useEffect(() => {
    setValue(quartier || "");
  }, [quartier]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled || !cityName}
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
            (disabled || !cityName) && "opacity-50 cursor-not-allowed",
            className
          )}
        >
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <MapPin className="h-4 w-4 text-primary flex-shrink-0 rtl:ml-2 ltr:mr-2" />
            <span className="truncate">
              {value || t("packages.form.customerSecteur")}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 flex-shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            className="h-9"
            placeholder={t("packages.form.searchQuartier")}
            value={searchTerm}
            onValueChange={setSearchTerm}
          />
          <CommandList className="max-h-[200px]">
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <span className="text-sm text-muted-foreground">
                  {t("common.loading")}...
                </span>
              </div>
            ) : (
              <>
                <CommandEmpty>
                  <div className="py-6 text-center text-sm">
                    {!cityName ? (
                      <span className="text-muted-foreground">
                        {t("packages.form.selectCityFirst")}
                      </span>
                    ) : suggestedQuartiers.length === 0 ? (
                      <span className="text-muted-foreground">
                        {t("packages.form.noQuartiersAvailable")}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">
                        {t("main.notFound", {
                          entity: t("packages.form.quartier"),
                        })}
                      </span>
                    )}
                  </div>
                </CommandEmpty>
                <CommandGroup>
                  {/* Show custom quartier creation option */}
                  {isCustomQuartier && (
                    <CommandItem
                      key="create-custom"
                      value={searchTerm}
                      onSelect={handleCreateCustom}
                      className="bg-muted/50"
                    >
                      <Plus className="mr-2 h-4 w-4 text-primary" />
                      <div className="flex-1 min-w-0">
                        <span className="truncate">
                          {t("packages.form.createQuartier")}: "
                          <strong>{searchTerm}</strong>"
                        </span>
                      </div>
                    </CommandItem>
                  )}

                  {/* Show filtered quartiers */}
                  {filteredQuartiers.map((q) => (
                    <CommandItem
                      key={q}
                      value={q}
                      onSelect={() => handleSelectQuartier(q)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === q ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="truncate">{q}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
